import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import TamilNaduMap from '../../components/TamilNaduMap/TamilNaduMap';
import { educationPrograms } from '../../data/educationData';
import './ProgramDetail.css';
import './EducationDetail.css';


const EducationDetail = () => {
    useScrollAnimation();
    const navigate = useNavigate();
    const [selectedProgram, setSelectedProgram] = useState(null);

    const handleBackClick = () => {
        navigate('/programs');
    };

    return (
        <div className="program-detail-page">
            <div className="program-hero">
                <div className="container">
                    <button onClick={handleBackClick} className="back-link">
                        <i className="fas fa-arrow-left"></i> Back to Programs
                    </button>
                    <div className="program-hero-content">
                        <div className="program-icon-hero">
                            <i className="fas fa-graduation-cap"></i> 
                        </div>
                        <h1>Education Program</h1>
                        <h1>கல்வி திட்டம்</h1>
                        <p className="program-tagline">Empowering minds through quality education</p>
                    </div>
                </div>
            </div>

            <div className="program-content">
                <div className="container">
                    <section className="program-section scroll-animate">
                        <h2>About Our Education Initiative</h2>
                        <p>Education is the strongest weapon against poverty, and every child deserves the chance to learn with dignity. Our education program focuses on providing free, quality education to underprivileged children in rural areas.</p>
                        <p>We believe that education is not just about academics, but also about character building, values, and preparing children for a better future. Through our 14 active branches, we reach out to communities where educational resources are scarce.</p>
                    </section>

                    {/* Map and Programs Overview Section */}
                    <section className="program-section programs-overview-section scroll-animate">
                        <h2>Our Education Programs Across Tamil Nadu</h2>
                        <p className="section-subtitle">Hover over any district to see what programs we offer there</p>
                        
                        <div className="map-programs-container">
                            {/* Left Side - Tamil Nadu Map */}
                            <div className="map-container">
                                <TamilNaduMap 
                                    highlightedDistricts={selectedProgram ? selectedProgram.districts : []}
                                    showDetailedPopup={true}
                                />
                            </div>

                            {/* Right Side - Programs by District */}
                            <div className="programs-cards-container">
                                <h3>Programs by District</h3>
                                <div className="programs-grid">
                                    {Object.values(educationPrograms).slice(0, 6).map((program) => (
                                        <div 
                                            key={program.id}
                                            className={`program-district-card ${selectedProgram?.id === program.id ? 'active' : ''}`}
                                            onClick={() => setSelectedProgram(selectedProgram?.id === program.id ? null : program)}
                                            style={{ borderColor: program.color }}
                                        >
                                            <div className="program-icon" style={{ color: program.color }}>
                                                {program.icon}
                                            </div>
                                            <div className="program-details">
                                                <div className="program-name-tamil">{program.name}</div>
                                                <div className="program-name-english">{program.nameEnglish}</div>
                                            </div>
                                            <div className="program-count" style={{ color: program.color }}>
                                                = {program.totalCenters || program.count}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {selectedProgram && (
                            <div className="selected-program-banner">
                                <p>
                                    <i className="fas fa-info-circle"></i>
                                    <strong>{selectedProgram.nameEnglish}</strong> is active in <strong>{selectedProgram.count} districts</strong>
                                    {selectedProgram.totalCenters && <span> with <strong>{selectedProgram.totalCenters} total centers</strong></span>}
                                    {' '}(highlighted on the map). Click any highlighted district to view details.
                                </p>
                            </div>
                        )}
                    </section>

                    <section className="program-section scroll-animate">
                        <h2>Key Achievements</h2>
                        <div className="achievements-grid">
                            <div className="achievement-card">
                                <div className="achievement-number">14+</div>
                                <div className="achievement-label">Active Branches</div>
                                <p>Operating in rural areas across multiple districts</p>
                            </div>
                            <div className="achievement-card">
                                <div className="achievement-number">800+</div>
                                <div className="achievement-label">Students Enrolled</div>
                                <p>Receiving free education and support</p>
                            </div>
                            <div className="achievement-card">
                                <div className="achievement-number">100%</div>
                                <div className="achievement-label">Free Education</div>
                                <p>No fees, including books and materials</p>
                            </div>
                        </div>
                    </section>

                    <section className="program-section scroll-animate">
                        <h2>What We Offer</h2>
                        <div className="features-list">
                            <div className="feature-item">
                                <i className="fas fa-check-circle"></i>
                                <div>
                                    <h3>Academic Support</h3>
                                    <p>Comprehensive tutoring and mentoring for all grade levels</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <i className="fas fa-check-circle"></i>
                                <div>
                                    <h3>Value-Based Learning</h3>
                                    <p>Character development and moral education integrated into curriculum</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <i className="fas fa-check-circle"></i>
                                <div>
                                    <h3>Free Study Materials</h3>
                                    <p>Books, stationery, and learning resources provided at no cost</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <i className="fas fa-check-circle"></i>
                                <div>
                                    <h3>Qualified Teachers</h3>
                                    <p>Dedicated volunteers and trained educators</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <i className="fas fa-check-circle"></i>
                                <div>
                                    <h3>Safe Learning Environment</h3>
                                    <p>Clean, secure spaces conducive to learning</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <i className="fas fa-check-circle"></i>
                                <div>
                                    <h3>Regular Assessments</h3>
                                    <p>Tracking progress and ensuring continuous improvement</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="program-section scroll-animate">
                        <h2>Program Impact</h2>
                        <div className="impact-stats">
                            <div className="impact-item">
                                <i className="fas fa-graduation-cap"></i>
                                <h3>Improved Performance</h3>
                                <p>85% of students show significant academic improvement within the first year</p>
                            </div>
                            <div className="impact-item">
                                <i className="fas fa-users"></i>
                                <h3>Community Engagement</h3>
                                <p>Parents and community members actively involved in children's education</p>
                            </div>
                            <div className="impact-item">
                                <i className="fas fa-star"></i>
                                <h3>Better Futures</h3>
                                <p>Many students have gone on to pursue higher education and successful careers</p>
                            </div>
                        </div>
                    </section>

                    <section className="program-section scroll-animate">
                        <h2>Weekly Activities</h2>
                        <div className="activities-timeline">
                            <div className="activity">
                                <div className="activity-day">Monday - Friday</div>
                                <div className="activity-content">
                                    <h3>Regular Classes</h3>
                                    <p>Core subjects: Mathematics, Science, Languages, Social Studies</p>
                                </div>
                            </div>
                            <div className="activity">
                                <div className="activity-day">Saturday</div>
                                <div className="activity-content">
                                    <h3>Special Sessions</h3>
                                    <p>Art, Music, Physical Education, and Life Skills training</p>
                                </div>
                            </div>
                            <div className="activity">
                                <div className="activity-day">Sunday</div>
                                <div className="activity-content">
                                    <h3>Values & Character</h3>
                                    <p>Moral education, storytelling, and personality development</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="program-cta scroll-animate">
                        <h2>Support Our Education Program</h2>
                        <p>Your contribution can help us reach more children and provide quality education to those who need it most</p>
                        <div className="cta-buttons">
                            <button onClick={() => navigate('/donate')} className="btn btn-primary btn-large">Donate Now</button>
                            <button onClick={() => navigate('/contact')} className="btn btn-secondary btn-large">Get Involved</button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default EducationDetail;
